import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MemberDialog from "@/components/admin/MemberDialog";
import { UserCog, CalendarDays, MapPin } from "lucide-react";

const STATUSES = ["new", "reviewing", "resolved", "action_taken", "dismissed"];
const tone = { new: "red", reviewing: "amber", resolved: "green", action_taken: "green", dismissed: "gray" };

export default function Reports() {
    const qc = useQueryClient();
    const [profileId, setProfileId] = useState(null);
    const [fStatus, setFStatus] = useState("all");
    const [fCat, setFCat] = useState("all");
    const [sortKey, setSortKey] = useState("newest");
    const { data } = useQuery({ queryKey: ["admin-reports"], queryFn: async () => (await api.get("/admin/reports")).data });
    const update = async (id, status) => { await api.put(`/admin/reports/${id}`, { status }); toast.success("Updated"); qc.invalidateQueries({ queryKey: ["admin-reports"] }); };

    const categories = useMemo(() => [...new Set((data || []).map((r) => r.category).filter(Boolean))], [data]);
    const rows = useMemo(() => {
        let arr = (data || []).filter((r) => (fStatus === "all" || r.status === fStatus) && (fCat === "all" || r.category === fCat));
        arr = [...arr];
        if (sortKey === "reporter") arr.sort((a, b) => (a.reporter_name || "").localeCompare(b.reporter_name || ""));
        else if (sortKey === "reported") arr.sort((a, b) => (a.reported_name || "").localeCompare(b.reported_name || ""));
        else if (sortKey === "status") arr.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
        else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        return arr;
    }, [data, fStatus, fCat, sortKey]);

    return (
        <div>
            <AdminHeader title="Reports" subtitle="Safety and member reports. Reporter identity is visible to admins only — never shown to the reported member." />
            <div className="mb-4 flex flex-wrap gap-2">
                <Select value={fStatus} onValueChange={setFStatus}>
                    <SelectTrigger data-testid="reports-filter-status" className="h-9 w-40"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={fCat} onValueChange={setFCat}>
                    <SelectTrigger data-testid="reports-filter-category" className="h-9 w-52"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={sortKey} onValueChange={setSortKey}>
                    <SelectTrigger data-testid="reports-sort" className="h-9 w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="newest">Newest first</SelectItem>
                        <SelectItem value="reporter">Reporter A–Z</SelectItem>
                        <SelectItem value="reported">Reported A–Z</SelectItem>
                        <SelectItem value="status">Status A–Z</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-3">
                {rows.length === 0 && <p className="text-sm text-zinc-500">No reports match these filters.</p>}
                {rows.map((r) => (
                    <div key={r.id} data-testid={`report-row-${r.id}`} className="rounded-lg border border-zinc-200 bg-white p-5">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="max-w-xl space-y-2">
                                <div className="flex items-center gap-2"><Badge tone="amber">{r.category}</Badge></div>
                                <div className="grid gap-1 text-sm">
                                    <p><span className="text-zinc-500">Reported member:</span> <span className="font-600">{r.reported_name}</span></p>
                                    <p data-testid={`report-reporter-${r.id}`}><span className="text-zinc-500">Reported by:</span> <span className="font-600">{r.reporter_name}</span>{r.reporter_email ? <span className="text-zinc-400"> · {r.reporter_email}</span> : null}</p>
                                    {r.outing && (
                                        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-zinc-600" data-testid={`report-outing-${r.id}`}>
                                            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-zinc-400" /> {r.outing.activity_emoji} {r.outing.activity_name} · {r.outing.date}</span>
                                            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-zinc-400" /> {r.outing.market}</span>
                                        </p>
                                    )}
                                </div>
                                <p className="mt-1 rounded bg-zinc-50 px-3 py-2 text-sm">{r.description}</p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <Badge tone={tone[r.status] || "gray"}>{r.status}</Badge>
                                <Select value={r.status} onValueChange={(v) => update(r.id, v)}>
                                    <SelectTrigger data-testid={`report-status-${r.id}`} className="h-9 w-44"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                                </Select>
                                {r.reported_id && (
                                    <Button size="sm" variant="outline" data-testid={`report-view-member-${r.id}`} onClick={() => setProfileId(r.reported_id)} className="text-red-600">
                                        <UserCog className="mr-1 h-4 w-4" /> View & block member
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <MemberDialog userId={profileId} onClose={() => setProfileId(null)} />
        </div>
    );
}
